import { ChevronDown, ChevronUp, CircleAlert, Pencil } from 'lucide-react';
import type React from 'react';
import { useEffect, useId, useState } from 'react';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import { Card, CardContent } from '@/components/shadcn/card';
import { Checkbox } from '@/components/shadcn/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible';
import { Field, FieldContent, FieldLabel } from '@/components/shadcn/field';
import { Label } from '@/components/shadcn/label';
import { govtIntakeFormLegacyDataLiveToday, govtIntakeFormOrganizationType, govtIntakeFormRole } from '@/data/govtIntakeStaticData.ts';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component.tsx';
import {
  type CurrentSystemsData,
  type EntryContactInfo,
  type PrimaryObjectOfWorkItem,
  type ProgramPainPointsData,
  type ProgramSubScopingData,
  type ProgramSubSections,
  type ProjectsPainPointsData,
  type ProjectsSubScopingData,
  type ProjectsSubSections,
  type PropertiesPainPointsData,
  type PropertiesSubNeedsData,
  type PropertiesSubSections,
  type PropertyLegacyConsolidationData,
  type RoleContext,
  type SystemData,
  useIntakeForm,
} from '@/modules/governmentIntake/context/intake-form-context';
import { capitalizeFirstLetter } from '@/utils';

type ReviewSectionKey =
  | 'entryContact'
  | 'context'
  | 'currentSystems'
  | 'discoveryFocus'
  | 'programScoping'
  | 'programPainPoints'
  | 'projectScoping'
  | 'projectPainPoints'
  | 'propertyScoping';

type NewReviewSubmitProps = {
  onValidationChange?: (isValid: boolean) => void;
  onEdit?: (section: string) => void;
};

const EMPTY_DASH = '—';

function FieldRow({ label, value }: { label: string; value: string | undefined | null }) {
  return (
    <Field orientation='vertical' className='gap-1'>
      <FieldLabel className='text-xs text-muted-foreground font-normal'>{label}</FieldLabel>
      <FieldContent className='p-0'>
        <p className='text-sm font-medium text-foreground'>{value?.trim() || EMPTY_DASH}</p>
      </FieldContent>
    </Field>
  );
}

function ReviewSectionCard({
  title,
  badge,
  badgeClassName,
  open,
  onOpenChange,
  onEdit,
  children,
}: {
  title: string;
  badge?: string;
  badgeClassName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <Card className='p-0 gap-0 bg-transparent border border-gray-300 shadow-none rounded-lg overflow-hidden'>
        <CardContent className='p-2'>
          <CollapsibleTrigger asChild>
            <Button
              type='button'
              className='flex w-full items-center bg-zinc-50 hover:bg-zinc-50 justify-between p-2 text-left transition-colors'
            >
              <div className='flex items-center py-4 gap-4'>
                <Label className='text-lg font-semibold text-foreground'>{title}</Label>
                {badge && (
                  <Badge
                    className={
                      badgeClassName
                        ? `border-2 rounded-lg text-xs ${badgeClassName}`
                        : 'mx-auto text-black border-2 border-gray-300 rounded-lg bg-gray-100 text-xs'
                    }
                  >
                    {badge}
                  </Badge>
                )}
              </div>
              <div className='flex items-center gap-2'>
                {onEdit && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer'
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                  >
                    <Pencil className='w-4 h-4 mr-1' />
                    Edit
                  </Button>
                )}
                {open ? <ChevronUp className='w-5 h-5 text-muted-foreground' /> : <ChevronDown className='w-5 h-5 text-muted-foreground' />}
              </div>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card className='p-4 mx-2 mb-2 mt-4 bg-white rounded-lg border shadow-none border-gray-300'>
              <CardContent className='p-0 '>{children}</CardContent>
            </Card>
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  );
}

function getEntryContactBadge(data: EntryContactInfo): string {
  const hasContact = data.email?.trim() ? true : Boolean(data.phone?.trim());
  const filled =
    (data.fullName?.trim() ? 1 : 0) + (hasContact ? 1 : 0) + (data.cityOrAgencyName?.trim() ? 1 : 0) + (data.department?.trim() ? 1 : 0);
  if (filled === 0) return 'Required';
  return filled >= 4 ? 'Complete' : 'Partial';
}

function getContextBadge(data: RoleContext): string {
  const filled =
    (data.roleDescription?.trim() ? 1 : 0) +
    (data.fromSeinTerminalPerspective?.trim() ? 1 : 0) +
    (data.involvedInApprovalRecommendation?.trim() ? 1 : 0);
  return filled >= 2 ? 'Provided' : filled > 0 ? 'Partial' : 'Incomplete';
}

function getCurrentSystemsBadge(data: CurrentSystemsData): string {
  const count = data?.systems?.length ?? 0;
  return count === 0 ? '0 documented' : `${count} documented`;
}

function getDiscoveryFocusBadge(primaryObjectOfWork: PrimaryObjectOfWorkItem[]): string {
  const hasSelection = primaryObjectOfWork?.length > 0;
  return hasSelection ? 'Selected' : 'Required';
}

function getProgramOrProjectScopingBadge(data: ProgramSubScopingData | ProjectsSubScopingData): string {
  const hasLegacy = data?.consolidate?.trim() || (data?.dataLocations?.length ?? 0) > 0;
  const hasNeeds =
    data?.automatedReporting === 'Yes' || data?.programForms === 'Yes' || data?.reportCount?.trim() || data?.formCount?.trim();
  const parts: string[] = [];
  if (hasLegacy) parts.push('Legacy');
  if (hasNeeds) parts.push('Needs');
  return parts.length > 0 ? parts.join(' + ') : 'Not provided';
}

//function getProjectScopingBadge(data: ProjectsSubScopingData): string {
//  const hasLegacy = data?.consolidate?.trim() || (data?.dataLocations?.length ?? 0) > 0;
//  const hasNeeds =
//      data?.automatedReporting === 'Yes' || data?.programForms === 'Yes' || data?.reportCount?.trim() || data?.formCount?.trim();
//  const parts: string[] = [];
//  if (hasLegacy) parts.push('Legacy');
//  if (hasNeeds) parts.push('Needs');
//  return parts.length > 0 ? parts.join(' + ') : 'Not provided';
//}

function getProgramPainPointsBadge(data: ProgramPainPointsData): string {
  const filled =
    (data.painPoint1?.trim() ? 1 : 0) +
    (data.painPoint2?.trim() ? 1 : 0) +
    (data.painPoint3?.trim() ? 1 : 0) +
    (data.successIn6To12Months?.trim() ? 1 : 0);
  return filled >= 2 ? 'Provided' : filled > 0 ? 'Partial' : 'Incomplete';
}

function getProjectPainPointsBadge(data: ProjectsPainPointsData): string {
  const filled =
    (data.painPoint1?.trim() ? 1 : 0) +
    (data.painPoint2?.trim() ? 1 : 0) +
    (data.painPoint3?.trim() ? 1 : 0) +
    (data.successIn6To12Months?.trim() ? 1 : 0);
  return filled >= 2 ? 'Provided' : filled > 0 ? 'Partial' : 'Incomplete';
}

function getPropertyScopingBadge(
  legacy: PropertyLegacyConsolidationData,
  needs: PropertiesSubNeedsData,
  pain: PropertiesPainPointsData
): string {
  const hasLegacy =
    legacy?.consolidate?.trim() ||
    Object.values(legacy?.propertyDataLive ?? {}).some(Boolean) ||
    legacy?.numberOfProperties?.trim() ||
    legacy?.approxParticipants?.trim() ||
    legacy?.approxDocumentsFiles?.trim() ||
    legacy?.dataQuality?.trim();
  const hasNeeds =
    needs?.automatedReporting === 'Yes' || needs?.programForms === 'Yes' || needs?.reportCount?.trim() || needs?.formCount?.trim();
  const hasPain =
    Boolean(pain?.painPoint1?.trim()) ||
    Boolean(pain?.painPoint2?.trim()) ||
    Boolean(pain?.painPoint3?.trim()) ||
    Boolean(pain?.leadershipAsksFor?.trim());
  const parts: string[] = [];
  if (hasLegacy) parts.push('Legacy');
  if (hasNeeds) parts.push('Needs');
  if (hasPain) parts.push('Pain');
  return parts.length > 0 ? parts.join(' + ') : 'Not provided';
}

function getRole(id: string): string {
  return govtIntakeFormRole.find((r) => r.id === id)?.id ?? '';
}

function getRoleLabel(id: string): string {
  return govtIntakeFormRole.find((r) => r.id === id)?.name ?? '';
}

function getOrgTypeLabel(value: string): string {
  return govtIntakeFormOrganizationType.find((o) => o.name === value)?.name ?? value;
}

function formatPropertyDataLiveLabels(propertyDataLive: Record<string, boolean>): string[] {
  return Object.entries(propertyDataLive ?? {})
    .filter(([, v]) => v)
    .map(([id]) => govtIntakeFormLegacyDataLiveToday.find((item) => item.id === id)?.label ?? id);
}

export default function NewReviewSubmit({ onValidationChange, onEdit }: NewReviewSubmitProps) {
  const { data } = useIntakeForm();
  const confirmId = useId();
  const [expandedSection, setExpandedSection] = useState<ReviewSectionKey | null>('entryContact');
  const [isConfirmed, setIsConfirmed] = useState(false);

  const selectedAreas = (data.primaryObjectOfWork ?? []).flatMap((o) => Object.keys(o));
  const hasPrograms = selectedAreas.includes('programs');
  const hasProjects = selectedAreas.includes('projects');
  const hasProperties = selectedAreas.includes('properties');

  const programsItem = data.primaryObjectOfWork?.find((item): item is { programs: ProgramSubSections } => 'programs' in item);
  const projectsItem = data.primaryObjectOfWork?.find((item): item is { projects: ProjectsSubSections } => 'projects' in item);
  const propertiesItem = data.primaryObjectOfWork?.find((item): item is { properties: PropertiesSubSections } => 'properties' in item);

  const programScopingData: ProgramSubScopingData = programsItem?.programs?.scoping ?? {
    anticipatedPrograms: '',
    automatedReporting: '',
    reportCount: '',
    automatedReportingFile: [],
    programForms: '',
    formCount: '',
    automatedFormFile: [],
    consolidate: '',
    dataLocations: [],
    numberOfProperties: '',
    approxParticipants: '',
    approxDocumentsFiles: '',
    dataQuality: '',
  };
  const programPainPointsData = programsItem?.programs?.painPoints ?? {
    biggestBurden: '',
    delaysAndErrors: '',
    hardestToProducе: '',
    painPoint1: '',
    painPoint2: '',
    painPoint3: '',
    successIn6To12Months: '',
  };
  const projectScopingData = projectsItem?.projects?.scoping ?? {
    anticipatedProjects: '',
    automatedReporting: '',
    reportCount: '',
    automatedReportingFile: [],
    programForms: '',
    formCount: '',
    automatedFormFile: [],
    consolidate: '',
    dataLocations: [],
    numberOfProperties: '',
    approxParticipants: '',
    approxDocumentsFiles: '',
    dataQuality: '',
  };
  const projectPainPointsData = projectsItem?.projects?.painPoints ?? {
    deptInvolved: '',
    externalPartnersInvolved: '',
    visibilityGaps: '',
    painPoint1: '',
    painPoint2: '',
    painPoint3: '',
    successIn6To12Months: '',
  };
  const legacyData = propertiesItem?.properties?.legacyConsolidation ?? {
    consolidate: '',
    propertyDataLive: {},
    numberOfProperties: '',
    approxParticipants: '',
    approxDocumentsFiles: '',
    dataQuality: '',
  };
  const needsData = propertiesItem?.properties?.needs ?? {
    automatedReporting: '',
    reportCount: '',
    automatedReportingFile: [],
    programForms: '',
    formCount: '',
    automatedFormFile: [],
  };
  const painPointsData = propertiesItem?.properties?.painPoints ?? {
    painPoint1: '',
    painPoint2: '',
    painPoint3: '',
    leadershipAsksFor: '',
  };

  const entryContactInfoFilled =
    (data.entryContact.fullName?.trim() ? 1 : 0) +
    (data.entryContact.email?.trim() ? 1 : 0) +
    (data.entryContact.cityOrAgencyName?.trim() ? 1 : 0) +
    (data.entryContact.department?.trim() ? 1 : 0);

  const hasEntryContactData = getEntryContactBadge(data.entryContact) === 'Complete';
  const hasPrimaryObjectOfWorkData = (data.primaryObjectOfWork ?? []).length > 0;
  const canSubmit = hasEntryContactData && hasPrimaryObjectOfWorkData && isConfirmed;

  useEffect(() => {
    onValidationChange?.(canSubmit);
  }, [canSubmit, onValidationChange]);

  const isEntryContactIncomplete = getEntryContactBadge(data.entryContact) !== 'Complete';
  const isDiscoveryFocusMissing = selectedAreas.length === 0;
  const showRequiredFieldsMessage = isEntryContactIncomplete || isDiscoveryFocusMissing;

  const setSectionOpen = (key: ReviewSectionKey, open: boolean) => {
    setExpandedSection(open ? key : null);
  };

  return (
    <div className='flex flex-col flex-1'>
      <header className='shrink-0 pt-4 pb-6'>
        <HeaderAndSub title='Review & Submit' subTitle='Expand a section to confirm details, or click Edit to make changes.' />
      </header>

      {showRequiredFieldsMessage && (
        <div className='mb-4 flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-3 text-sm text-red-600'>
          <CircleAlert className='h-4 w-4 text-red-600' />
          <div>Please complete all required fields before submitting. Please select at least one area of work.</div>
        </div>
      )}

      <section className='flex-1 space-y-4 pb-8'>
        {/* (1) Entry & Contact Info - from Contact Information section */}
        <ReviewSectionCard
          title='Entry & Contact Info'
          badge={getEntryContactBadge(data.entryContact ?? {})}
          badgeClassName={entryContactInfoFilled === 0 ? 'flex  bg-red-600 text-white border-red-600 hover:bg-red-600' : undefined}
          open={expandedSection === 'entryContact'}
          onOpenChange={(open) => setSectionOpen('entryContact', open)}
          onEdit={() => onEdit?.('contact')}
        >
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='space-y-4'>
              <FieldRow label='Name' value={data.entryContact.fullName} />
              <FieldRow label='Organization' value={data.entryContact.cityOrAgencyName} />
              <FieldRow label='State' value={data.entryContact.state} />
              <FieldRow label='Email' value={data.entryContact.email} />
              <FieldRow label='Preferred Contact' value={data.entryContact.preferredContact} />
            </div>
            <div className='space-y-4'>
              <FieldRow label='Title' value={data.entryContact.jobTitle} />
              <FieldRow label='Type' value={getOrgTypeLabel(data.entryContact.organizationType)} />
              <FieldRow label='Department' value={data.entryContact.department} />
              <FieldRow label='Phone' value={data.entryContact.phone} />
            </div>
          </div>
        </ReviewSectionCard>

        {/* (2) Context - from Role & Context section */}
        <ReviewSectionCard
          title='Context'
          badge={getContextBadge(data.roleContext)}
          open={expandedSection === 'context'}
          onOpenChange={(open) => setSectionOpen('context', open)}
          onEdit={() => onEdit?.('role')}
        >
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='space-y-4'>
              <FieldRow label='Primary Role' value={capitalizeFirstLetter(getRole(data.roleContext.roleDescription))} />
              <FieldRow label='Role Type' value={getRoleLabel(data.roleContext.roleDescription)} />
              <FieldRow label='Problem to Solve' value={data.roleContext.fromSeinTerminalPerspective} />
            </div>
            <div className='space-y-4'>
              <FieldRow label='Data Interaction' value={data.roleContext.fromSeinTerminalPerspective} />
              <FieldRow label='Approval Involvement' value={data.roleContext.involvedInApprovalRecommendation} />
            </div>
          </div>
        </ReviewSectionCard>

        {/* (3) Current Systems - from Current Systems section */}
        <ReviewSectionCard
          title='Current Systems'
          badge={getCurrentSystemsBadge(data.currentSystems)}
          open={expandedSection === 'currentSystems'}
          onOpenChange={(open) => setSectionOpen('currentSystems', open)}
          onEdit={() => onEdit?.('systems')}
        >
          <div className='space-y-2'>
            {(data.currentSystems?.systems ?? []).length > 0 ? (
              (data.currentSystems?.systems ?? []).map((sys: SystemData) => (
                <div key={sys.id} className='rounded-lg bg-gray-50 px-4 py-3 text-sm font-medium text-gray-900'>
                  {sys.name || 'Unnamed system'}
                </div>
              ))
            ) : (
              <p className='text-sm text-gray-400'>No systems documented yet.</p>
            )}
          </div>
        </ReviewSectionCard>

        {/* (4) Discovery Focus - from Primary Object of Work, root keys only */}
        <ReviewSectionCard
          title='Discovery Focus'
          badge={getDiscoveryFocusBadge(data.primaryObjectOfWork ?? [])}
          badgeClassName={selectedAreas.length === 0 ? 'bg-red-600 text-white border-red-600 hover:bg-red-600' : undefined}
          open={expandedSection === 'discoveryFocus'}
          onOpenChange={(open) => setSectionOpen('discoveryFocus', open)}
          onEdit={() => onEdit?.('object')}
        >
          <div className='space-y-2'>
            <p className='text-xs text-gray-500 mb-2'>Selected areas of work</p>
            <div className='flex flex-wrap gap-2'>
              {selectedAreas.length !== 0 &&
                selectedAreas.map((area) => (
                  <Badge key={area} className='inline-block borde-2 border-gray-300 bg-gray-200 text-black rounded-lg'>
                    {area === 'programs'
                      ? 'Programs'
                      : area === 'projects'
                        ? 'Projects'
                        : area === 'properties'
                          ? 'Properties'
                          : area.charAt(0).toUpperCase() + area.slice(1)}
                  </Badge>
                ))}
              {selectedAreas.length === 0 && <p className='text-sm text-gray-400 italic'>None selected</p>}
            </div>
          </div>
        </ReviewSectionCard>

        {/* Program Scoping - when programs selected in discovery focus */}
        {hasPrograms && (
          <ReviewSectionCard
            title='Program Scoping'
            badge={getProgramOrProjectScopingBadge(programScopingData)}
            open={expandedSection === 'programScoping'}
            onOpenChange={(open) => setSectionOpen('programScoping', open)}
            onEdit={() => onEdit?.('programs-scoping')}
          >
            <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
              <div className='space-y-4'>
                <h4 className='text-sm font-semibold text-gray-900'>Legacy Data</h4>
                <FieldRow label='Data consolidation needed' value={programScopingData.consolidate || EMPTY_DASH} />
                <div>
                  <p className='text-xs text-gray-500 mb-2'>Data locations</p>
                  <div className='flex flex-wrap gap-2'>
                    {(programScopingData.dataLocations ?? []).map((loc) => (
                      <Badge key={loc} variant='outline' className='font-normal'>
                        {loc}
                      </Badge>
                    ))}
                    {(programScopingData.dataLocations ?? []).length === 0 && (
                      <span className='text-sm text-gray-400 italic'>None specified</span>
                    )}
                  </div>
                </div>
              </div>
              <div className='space-y-4'>
                <h4 className='text-sm font-semibold text-gray-900'>Platform Needs</h4>
                <div className='space-y-1'>
                  <Label className='text-gray-500 text-xs'>Automated Reporting</Label>
                  <Label>{programScopingData.automatedReporting || 'No'}</Label>
                  <Label className='text-xs text-gray-400'>
                    Count: <span className='text-gray-500'>{programScopingData.reportCount}</span>
                  </Label>
                </div>

                <div className='space-y-1'>
                  <Label className='text-gray-500 text-xs'>Forms Needed</Label>
                  <Label>{programScopingData.programForms || 'No'}</Label>
                  <Label className='text-xs text-gray-400'>
                    Count: <span className='text-gray-500'>{programScopingData.formCount}</span>
                  </Label>
                </div>
                {/*<FieldRow label="Automated reporting"*/}
                {/*    value={programScopingData.automatedReporting || 'No'}/>*/}
                {/*<FieldRow label="Report count" value={programScopingData.reportCount}/>*/}
                {/*<FieldRow label="Forms needed" value={programScopingData.programForms || 'No'}/>*/}
                {/*<FieldRow label="Form count" value={programScopingData.formCount}/>*/}
              </div>
            </div>
          </ReviewSectionCard>
        )}

        {/* Program Pain Points - when programs selected */}
        {hasPrograms && (
          <ReviewSectionCard
            title='Program Pain Points'
            badge={getProgramPainPointsBadge(programPainPointsData)}
            open={expandedSection === 'programPainPoints'}
            onOpenChange={(open) => setSectionOpen('programPainPoints', open)}
            onEdit={() => onEdit?.('programs-painPoints')}
          >
            <div className='space-y-4'>
              <FieldRow label='Pain Point 1' value={programPainPointsData.painPoint1} />
              <FieldRow label='Pain Point 2' value={programPainPointsData.painPoint2} />
              <FieldRow label='Pain Point 3' value={programPainPointsData.painPoint3} />
              <FieldRow label='What success looks like' value={programPainPointsData.successIn6To12Months} />
            </div>
          </ReviewSectionCard>
        )}

        {/* Project Scoping - when projects selected in discovery focus */}
        {hasProjects && (
          <ReviewSectionCard
            title='Project Scoping'
            badge={getProgramOrProjectScopingBadge(projectScopingData)}
            open={expandedSection === 'projectScoping'}
            onOpenChange={(open) => setSectionOpen('projectScoping', open)}
            onEdit={() => onEdit?.('projects-scoping')}
          >
            <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
              <div className='space-y-4'>
                <h4 className='text-sm font-semibold text-gray-900'>Legacy Data</h4>
                <FieldRow label='Data consolidation needed' value={projectScopingData.consolidate || EMPTY_DASH} />
                <div>
                  <p className='text-xs text-gray-500 mb-2'>Data locations</p>
                  <div className='flex flex-wrap gap-2'>
                    {(projectScopingData.dataLocations ?? []).map((loc) => (
                      <Badge key={loc} variant='outline' className='font-normal'>
                        {loc}
                      </Badge>
                    ))}
                    {(projectScopingData.dataLocations ?? []).length === 0 && (
                      <span className='text-sm text-gray-400 italic'>None specified</span>
                    )}
                  </div>
                </div>
              </div>
              <div className='space-y-4'>
                <h4 className='text-sm font-semibold text-gray-900'>Platform Needs</h4>
                <FieldRow label='Automated reporting' value={projectScopingData.automatedReporting || 'No'} />
                <FieldRow label='Report count' value={projectScopingData.reportCount} />
                <FieldRow label='Forms needed' value={projectScopingData.programForms || 'No'} />
                <FieldRow label='Form count' value={projectScopingData.formCount} />
              </div>
            </div>
          </ReviewSectionCard>
        )}

        {/* Project Pain Points - when projects selected */}
        {hasProjects && (
          <ReviewSectionCard
            title='Project Pain Points'
            badge={getProjectPainPointsBadge(projectPainPointsData)}
            open={expandedSection === 'projectPainPoints'}
            onOpenChange={(open) => setSectionOpen('projectPainPoints', open)}
            onEdit={() => onEdit?.('projects-painPoints')}
          >
            <div className='space-y-4'>
              <FieldRow label='Dept involved' value={projectPainPointsData.deptInvolved} />
              <FieldRow label='External partners involved' value={projectPainPointsData.externalPartnersInvolved} />
              <FieldRow label='Visibility gaps' value={projectPainPointsData.visibilityGaps} />
              <FieldRow label='Pain Point 1' value={projectPainPointsData.painPoint1} />
              <FieldRow label='Pain Point 2' value={projectPainPointsData.painPoint2} />
              <FieldRow label='Pain Point 3' value={projectPainPointsData.painPoint3} />
              <FieldRow label='What success looks like' value={projectPainPointsData.successIn6To12Months} />
            </div>
          </ReviewSectionCard>
        )}

        {/* Property Scoping - Legacy Data (left) + Property Needs (right), when properties selected */}
        {hasProperties && (
          <ReviewSectionCard
            title='Property Scoping'
            badge={getPropertyScopingBadge(legacyData, needsData, painPointsData)}
            open={expandedSection === 'propertyScoping'}
            onOpenChange={(open) => setSectionOpen('propertyScoping', open)}
          >
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <h4 className='text-sm font-semibold text-gray-900'>Legacy Data & Consolidation</h4>
                  {onEdit && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='text-muted-foreground hover:text-foreground hover:bg-muted'
                      onClick={() => onEdit('properties-legacyConsolidation')}
                    >
                      <Pencil className='w-4 h-4 mr-1' />
                      Edit
                    </Button>
                  )}
                </div>
                <FieldRow label='Ingest existing data' value={legacyData.consolidate || EMPTY_DASH} />
                <FieldRow label='Number of properties' value={legacyData.numberOfProperties} />
                <FieldRow label='Approx. participants' value={legacyData.approxParticipants} />
                <FieldRow label='Approx. documents/files' value={legacyData.approxDocumentsFiles} />
                <FieldRow label='Data quality' value={legacyData.dataQuality} />
                <div>
                  <p className='text-xs text-gray-500 mb-2'>Where property data lives today</p>
                  <div className='flex flex-wrap gap-2'>
                    {formatPropertyDataLiveLabels(legacyData.propertyDataLive).map((loc) => (
                      <Badge key={loc} variant='outline' className='font-normal'>
                        {loc}
                      </Badge>
                    ))}
                    {formatPropertyDataLiveLabels(legacyData.propertyDataLive).length === 0 && (
                      <span className='text-sm text-gray-400 italic'>None specified</span>
                    )}
                  </div>
                </div>
              </div>
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <h4 className='text-sm font-semibold text-gray-900'>Property needs</h4>
                  {onEdit && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='text-muted-foreground hover:text-foreground hover:bg-muted'
                      onClick={() => onEdit('properties-needs')}
                    >
                      <Pencil className='w-4 h-4 mr-1' />
                      Edit
                    </Button>
                  )}
                </div>
                <FieldRow label='Automated reporting' value={needsData.automatedReporting || 'No'} />
                <FieldRow label='Report count' value={needsData.reportCount} />
                <FieldRow label='Forms needed' value={needsData.programForms || 'No'} />
                <FieldRow label='Form count' value={needsData.formCount} />
              </div>
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <h4 className='text-sm font-semibold text-gray-900'>Pain points & outcomes</h4>
                  {onEdit && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='text-muted-foreground hover:text-foreground hover:bg-muted'
                      onClick={() => onEdit('properties-needs')}
                    >
                      <Pencil className='w-4 h-4 mr-1' />
                      Edit
                    </Button>
                  )}
                </div>
                <FieldRow label='Pain Point 1' value={painPointsData.painPoint1} />
                <FieldRow label='Pain Point 2' value={painPointsData.painPoint2} />
                <FieldRow label='Pain Point 3' value={painPointsData.painPoint3} />
                <FieldRow label='Leadership asks for' value={painPointsData.leadershipAsksFor} />
              </div>
            </div>
          </ReviewSectionCard>
        )}

        {/* Confirmation */}
        <Card className='border border-border shadow-sm rounded-lg p-6 mt-6'>
          <div className='flex items-start gap-3'>
            <Checkbox
              id={confirmId}
              checked={isConfirmed}
              onCheckedChange={(checked) => setIsConfirmed(checked === true)}
              className='mt-1'
            />
            <div className='flex-1'>
              <Label htmlFor={confirmId} className='text-sm font-medium cursor-pointer leading-relaxed'>
                I confirm that the information provided is accurate to the best of my knowledge and may be used by SEIN Terminal to scope
                configuration, identify integration requirements, and inform pricing. I understand this intake will be aggregated with
                responses from other departments to design a unified solution.
              </Label>
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
}
